// 字段管理服务
export class FieldService {
  
  /**
   * 创建表格元素
   * @param {Array} selectedFields - 选中的字段
   * @returns {Object} 表格元素
   */
  static createTableElement(selectedFields) {
    const columns = selectedFields.map(field => ({
      id: field.id,
      title: field.displayName || field.name,
      width: this.getDefaultColumnWidth(field.id),
      dataType: this.getFieldDataType(field.id)
    }))
    
    return {
      id: `table_${Date.now()}`,
      type: 'table',
      columns,
      rowHeight: 32,
      data: this.generateMockTableData(columns),
      style: {
        position: 'absolute',
        left: '50px',
        top: '100px',
        border: '1px solid #999',
        backgroundColor: 'rgba(255,255,255,0.95)',
        minWidth: '200px'
      }
    }
  }
  
  /**
   * 创建文本元素
   * @param {Object} field - 字段信息
   * @param {Object} position - 位置信息
   * @returns {Object} 文本元素
   */
  static createTextElement(field, position = { x: 50, y: 50 }) {
    return {
      id: `text_${field.id}_${Date.now()}`,
      type: 'text',
      fieldId: field.id,
      content: field.displayName || field.name,
      style: {
        position: 'absolute',
        left: position.x + 'px',
        top: position.y + 'px',
        fontSize: '14px',
        fontFamily: '微软雅黑',
        color: '#333',
        padding: '4px 8px',
        border: '1px dashed #ccc',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        minWidth: '80px',
        minHeight: '24px',
        cursor: 'move'
      }
    }
  }
  
  /**
   * 获取字段默认列宽
   * @param {string} fieldId - 字段ID
   * @returns {number} 列宽
   */
  static getDefaultColumnWidth(fieldId) {
    const widthMap = {
      'product_code': 100,
      'product_name': 150,
      'primary_category': 120,
      'secondary_category': 120,
      'product_brand': 100,
      'barcode': 80,
      'production_date': 100,
      'shelf_life': 80,
      'order_number': 140,
      'customer_name': 120,
      'shipping_address': 200
    }
    
    return widthMap[fieldId] || 100
  }
  
  /**
   * 获取字段数据类型
   * @param {string} fieldId - 字段ID
   * @returns {string} 数据类型
   */
  static getFieldDataType(fieldId) {
    const typeMap = {
      'product_code': 'string',
      'product_name': 'string',
      'primary_category': 'string',
      'secondary_category': 'string',
      'product_brand': 'string',
      'barcode': 'barcode',
      'production_date': 'date',
      'shelf_life': 'string',
      'order_number': 'string',
      'customer_name': 'string',
      'total_order_qty': 'number',
      'total_shipment_qty': 'number',
      'order_weight': 'decimal',
      'actual_total_price': 'currency'
    }
    
    return typeMap[fieldId] || 'string'
  }
  
  /**
   * 生成模拟表格数据
   * @param {Array} columns - 列配置
   * @returns {Array} 模拟数据
   */
  static generateMockTableData(columns) {
    const mockDataMap = {
      'product_code': ['P001', 'P002', 'P003', 'P004'],
      'product_name': ['有机苹果', '进口香蕉', '赣南橙子', '新疆葡萄'],
      'primary_category': ['水果', '水果', '水果', '水果'],
      'secondary_category': ['苹果类', '香蕉类', '柑橘类', '葡萄类'],
      'product_brand': ['红富士', '都乐', '赣南', '吐鲁番'],
      'barcode': ['|||||||||||', '|||||||||||', '|||||||||||', '|||||||||||'],
      'production_date': ['2023-10-25', '2023-10-26', '2023-10-24', '2023-10-23'],
      'shelf_life': ['7天', '5天', '10天', '15天'],
      'total_order_qty': ['10', '5', '8', '12'],
      'order_weight': ['5.0kg', '2.5kg', '4.0kg', '6.0kg']
    }
    
    const rows = []
    for (let i = 0; i < 4; i++) {
      const row = {}
      columns.forEach(col => {
        const mockValues = mockDataMap[col.id]
        row[col.id] = mockValues ? mockValues[i] : `示例${i + 1}`
      })
      rows.push(row)
    }
    
    return rows
  }
  
  /**
   * 搜索字段
   * @param {Object} fieldCategories - 字段分类
   * @param {string} keyword - 搜索关键词
   * @returns {Object} 搜索结果
   */
  static searchFields(fieldCategories, keyword) {
    if (!keyword) return fieldCategories
    
    const lowerKeyword = keyword.toLowerCase()
    const result = {}
    
    Object.entries(fieldCategories).forEach(([categoryName, category]) => {
      const filteredFields = category.fields.filter(field => {
        const matchesName = field.name.toLowerCase().includes(lowerKeyword)
        const matchesDisplayName = (field.displayName || '').toLowerCase().includes(lowerKeyword)
        const matchesSubFields = field.subFields ? 
          field.subFields.some(sf => 
            sf.name.toLowerCase().includes(lowerKeyword) ||
            (sf.displayName || '').toLowerCase().includes(lowerKeyword)
          ) : false
        
        return matchesName || matchesDisplayName || matchesSubFields
      })
      
      if (filteredFields.length > 0) {
        result[categoryName] = {
          ...category,
          fields: filteredFields
        }
      }
    })
    
    return result
  }
  
  /**
   * 验证字段配置
   * @param {Array} selectedFields - 选中的字段
   * @returns {Object} 验证结果
   */
  static validateFields(selectedFields) {
    const errors = []
    const warnings = []
    
    if (selectedFields.length === 0) {
      warnings.push('未选择任何字段')
    }
    
    // 检查重复字段
    const fieldIds = selectedFields.map(f => f.id)
    const duplicates = fieldIds.filter((id, index) => fieldIds.indexOf(id) !== index)
    if (duplicates.length > 0) {
      errors.push(`存在重复字段: ${duplicates.join(', ')}`)
    }
    
    // 检查显示名称
    selectedFields.forEach(field => {
      if (!field.displayName || field.displayName.trim() === '') {
        warnings.push(`字段 "${field.name}" 缺少显示名称`)
      }
    })
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }
}
